import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { 
  AssemblyCodeItem,
  AssemblyRecordDto,
  AssemblyValidationResult
} from '../interfaces/assembly-consultation.interface';
import { URL_SERVICIOS, HTTP_OPTIONS } from '../shared/config/config';

export interface ProcessExcelResult {
  blob: Blob;
  filename: string;
  size: number;
}

@Injectable({
  providedIn: 'root'
})
export class AssemblyConsultationService {

  private baseUrl = `${URL_SERVICIOS}AssemblyConsultation`;

  constructor(private http: HttpClient) { }

  /**
   * Valida archivo Excel con participantes de asamblea
   * Endpoint: POST /api/AssemblyConsultation/validate-excel
   */
  validateExcel(file: File): Observable<AssemblyValidationResult> {
    const userToken = JSON.parse(sessionStorage.getItem('userToken') || 'null');
    const token = userToken?.token || '';

    return new Observable<AssemblyValidationResult>(observer => {
      const formData = new FormData();
      formData.append('file', file, file.name);

      fetch(`${this.baseUrl}/validate-excel`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      })
      .then(async response => {
        const data = await response.json();
        if (response.ok) {
          observer.next(data);
          observer.complete();
        } else {
          observer.error({ userMessage: data.message || 'Error al validar el archivo', details: data });
        }
      })
      .catch(() => observer.error({ userMessage: 'Error de conexión al servidor' }));
    });
  }

  /**
   * Procesa archivo Excel: valida + consulta Experian + retorna reporte Excel
   * Endpoint: POST /api/AssemblyConsultation/process-excel
   * Descarga el archivo automáticamente y retorna metadata
   */
  processExcel(file: File): Observable<ProcessExcelResult> {
    const userToken = JSON.parse(sessionStorage.getItem('userToken') || 'null');
    const token = userToken?.token || '';

    return new Observable<ProcessExcelResult>(observer => {
      const formData = new FormData();
      formData.append('file', file, file.name);

      fetch(`${this.baseUrl}/process-excel`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      })
      .then(async response => {
        if (response.ok) {
          observer.next({ blob: null, filename: null, size: 0 });
          observer.complete();
        } else {
          const text = await response.text();
          try {
            const errorData = JSON.parse(text);
            if (errorData.status === 'validation_failed') {
              observer.error({
                status: errorData.status,
                validationResult: errorData.validationResult,
                userMessage: errorData.message
              });
            } else {
              observer.error({ userMessage: errorData.message || 'Error al procesar el archivo', httpStatus: response.status });
            }
          } catch {
            observer.error({ userMessage: `Error ${response.status}: ${response.statusText}`, httpStatus: response.status });
          }
        }
      })
      .catch(error => {
        console.error('Error de red:', error);
        observer.error({ userMessage: 'Error de conexión al servidor.' });
      });
    });
  }

  private formatDate(): string {
    const d = new Date();
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    return `${dd}-${mm}-${yyyy}_${hh}${min}`;
  }

  private extractFilename(contentDisposition: string | null): string | null {
    if (!contentDisposition) return null;
    const matches = /filename[^;=\n]*=(['"]?)([^'"\n;]*)\1/.exec(contentDisposition);
    return matches?.[2]?.trim() || null;
  }

  private downloadFile(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => window.URL.revokeObjectURL(url), 100);
  }

  /**
   * Descarga el reporte Excel de una asamblea ya procesada
   * Endpoint: GET /api/AssemblyConsultation/{assemblyCode}/download
   */
  downloadReport(assemblyCode: string): Observable<ProcessExcelResult> {
    const userToken = JSON.parse(sessionStorage.getItem('userToken') || 'null');
    const token = userToken?.token || '';

    return new Observable<ProcessExcelResult>(observer => {
      fetch(`${this.baseUrl}/${encodeURIComponent(assemblyCode)}/download`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` }
      })
      .then(async response => {
        if (response.ok) {
          const contentDisposition = response.headers.get('Content-Disposition');
          const contentLength = response.headers.get('Content-Length');
          const filename = this.extractFilename(contentDisposition) || `Qualities_${assemblyCode}_${this.formatDate()}.xlsx`;
          const size = contentLength ? parseInt(contentLength, 10) : 0;

          const blob = await response.blob();

          if (!blob || blob.size === 0) {
            observer.error({ userMessage: 'El archivo descargado está vacío. Por favor, intenta nuevamente.' });
            return;
          }

          this.downloadFile(blob, filename);
          observer.next({ blob, filename, size });
          observer.complete();
        } else {
          const text = await response.text();
          try {
            const errorData = JSON.parse(text);
            observer.error({ userMessage: errorData.message || 'Error al descargar el reporte', httpStatus: response.status });
          } catch {
            observer.error({ userMessage: `Error ${response.status}: ${response.statusText}`, httpStatus: response.status });
          }
        }
      })
      .catch(error => {
        console.error('Error de red:', error);
        observer.error({ userMessage: 'Error de conexión al servidor.' });
      });
    });
  }

  /**
   * Obtiene el listado de asambleas agrupadas por código
   * Endpoint: GET /api/AssemblyConsultation
   */
  getAssemblies(): Observable<AssemblyCodeItem[]> {
    return this.http.get<AssemblyCodeItem[]>(`${this.baseUrl}`, HTTP_OPTIONS)
      .pipe(catchError(this.handleError));
  }

  /**
   * Obtiene registros de una asamblea específica
   * Endpoint: GET /api/AssemblyConsultation/{assemblyCode}/records
   */
  getAssemblyRecords(assemblyCode: string): Observable<AssemblyRecordDto[]> {
    return this.http.get<AssemblyRecordDto[]>(`${this.baseUrl}/${assemblyCode}/records`, HTTP_OPTIONS)
      .pipe(catchError(this.handleError));
  }

  /**
   * Manejo de errores HTTP
   */
  private handleError(error: any): Observable<never> {
    console.error('Error en AssemblyConsultationService:', error);

    let errorMessage = 'Ha ocurrido un error en el servidor';

    if (typeof error === 'string' && error.length > 0) {
      errorMessage = error;
    } else if (error.error instanceof ErrorEvent) {
      errorMessage = error.error.message;
    } else if (error?.message) {
      errorMessage = error.message;
    } else if (error?.error?.message) {
      errorMessage = error.error.message;
    } else if (error?.status) {
      errorMessage = `Error ${error.status}: ${error.statusText}`;
    }

    return throwError(() => ({ userMessage: errorMessage, status: error.status }));
  }
}
